import type { UIView } from "uicore-ts"
/**
 * Wraps a raw `HTMLElement` so it can participate in the `RHFocusManager` /
 * `RHPageFocusManager` Tab-navigation system alongside `UIView` instances.
 *
 * The focus system normally works exclusively with `UIView` objects. This class
 * provides a minimal duck-type shim that satisfies every property `RHFocusManager`
 * and `RHPageFocusManager` read from their items:
 *
 * - `focus()` / `viewHTMLElement` — used by `_focusItem` to move DOM focus.
 * - `superview`, `isMemberOfViewTree` — used by `_activeItems` to confirm the
 *   element is actually in the live document.
 * - `enabled` — used by `_activeItems`; always `true` unless the element is
 *   a form control that is `disabled`.
 * - `withAllSuperviews` — used by `_activeItems` to check for hidden ancestors;
 *   returns an empty array (native elements have no UIView ancestors to check).
 * - `hidden` — always `false`; callers should simply not include items that
 *   should not be reachable.
 *
 * ### Containment vs. focus target
 * `element` is used for two purposes: containment matching in `handleTabEvent`
 * (to detect which item currently has focus) and as the DOM element that receives
 * `.focus()` when Tab navigates here.
 *
 * When those two elements differ — for example, the day grid where containment
 * should match the `.flatpickr-days` container but focus should land on the
 * currently-highlighted day cell inside it — supply a `focusTarget` callback.
 * `focus()` calls `focusTarget()` instead of `element.focus()`.
 *
 * ### Usage
 * ```typescript
 * // Simple: single element is both the match target and focus target
 * items.push(new RHNativeFocusItem(hoursInput))
 *
 * // Container match, custom focus target (e.g. day grid)
 * const dayContainer = container.querySelector(".flatpickr-days")
 * items.push(new RHNativeFocusItem(dayContainer, () =>
 *     container.querySelector(".flatpickr-day.selected, .flatpickr-day.today")
 *         ?? container.querySelector(".flatpickr-day:not(.flatpickr-disabled)")
 * ))
 * ```
 */
export class RHNativeFocusItem {
    
    /** The element used for containment matching in `handleTabEvent`. */
    readonly element: HTMLElement
    
    /**
     * Optional callback returning the element that should actually receive
     * DOM focus when Tab navigates to this stop. When absent, `element` is
     * focused directly.
     */
    readonly focusTarget?: () => HTMLElement | null | undefined
    
    constructor(element: HTMLElement, focusTarget?: () => HTMLElement | null | undefined) {
        this.element = element
        this.focusTarget = focusTarget
    }
    
    // ── Duck-type interface consumed by RHFocusManager / RHPageFocusManager ──
    
    /** Moves DOM focus to the focus target (or the element itself if no target is provided). */
    focus() {
        const target = this.focusTarget ? this.focusTarget() : this.element
        if (!target) { return }
        if (!target.hasAttribute("tabindex")) {
            target.setAttribute("tabindex", "-1")
        }
        target.focus()
    }
    
    /** Satisfies the `viewHTMLElement` contract used by `_focusItem`. */
    get viewHTMLElement(): HTMLElement {
        return this.element
    }
    
    /**
     * Truthy when the element is attached to the live document — mirrors the
     * `superview` check that `_activeItems` performs on UIView instances.
     */
    get superview(): ParentNode | null {
        return this.element.parentNode
    }
    
    /**
     * Mirrors `UIView.isMemberOfViewTree`. True when the element is inside
     * the document.
     */
    get isMemberOfViewTree(): boolean {
        return document.contains(this.element)
    }
    
    /**
     * Mirrors `UIView.enabled`. Reads the `disabled` attribute for form
     * controls; always true for non-form elements.
     */
    get enabled(): boolean {
        if (
            this.element instanceof HTMLButtonElement ||
            this.element instanceof HTMLInputElement ||
            this.element instanceof HTMLSelectElement ||
            this.element instanceof HTMLTextAreaElement
        ) {
            return !this.element.disabled
        }
        return true
    }
    
    /**
     * Mirrors `UIView.withAllSuperviews`. Returns an empty array — native
     * elements have no UIView ancestors whose `hidden` state needs checking.
     */
    get withAllSuperviews(): UIView[] {
        return []
    }
    
    /** Always false — exclude items that should not be reachable instead. */
    get hidden(): false {
        return false
    }
    
}
